import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { cabApi } from '../../services/api'
import toast from 'react-hot-toast'
import { Search, Download, RotateCcw, Ban, X } from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'

export default function CabArchivio() {
  const qc = useQueryClient()
  const { user } = useAuth()
  const isAdmin = ['admin', 'superadmin'].includes((user as any)?.role)
  const [search, setSearch] = useState('')
  const [selezionata, setSelezionata] = useState<any | null>(null)
  const [motivoModal, setMotivoModal] = useState<'annulla' | 'ristampa' | null>(null)
  const [motivo, setMotivo] = useState('')
  const [esportaDa, setEsportaDa] = useState('')
  const [esportaA, setEsportaA] = useState('')

  const { data, isLoading } = useQuery({
    queryKey: ['cab-etichette', search],
    queryFn: () => cabApi.list({ search: search || undefined, per_page: 50 }).then(r => r.data),
  })

  const annullaMut = useMutation({
    mutationFn: () => cabApi.annulla(selezionata.id, motivo),
    onSuccess: () => { toast.success('Etichetta annullata'); qc.invalidateQueries({ queryKey: ['cab-etichette'] }); setMotivoModal(null); setMotivo('') },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Errore'),
  })
  const ristampaMut = useMutation({
    mutationFn: () => cabApi.ristampa(selezionata.id, { motivo }),
    onSuccess: () => { toast.success('Ristampa registrata'); qc.invalidateQueries({ queryKey: ['cab-etichette'] }); setMotivoModal(null); setMotivo('') },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Errore'),
  })

  const scarica = async (tipo: 'zpl' | 'svg' | 'pdf', id: string, codice: string) => {
    const fn = tipo === 'zpl' ? cabApi.downloadZpl : tipo === 'svg' ? cabApi.downloadSvg : cabApi.downloadPdf
    const res = await fn(id)
    const url = URL.createObjectURL(res.data)
    const a = document.createElement('a')
    a.href = url; a.download = `${codice}.${tipo}`
    a.click()
    URL.revokeObjectURL(url)
  }

  const items = data?.items ?? []

  const esportaElenco = async (formato: 'csv' | 'xlsx') => {
    if (!esportaDa || !esportaA) { toast.error('Specificare l\'intervallo di date'); return }
    const res = await cabApi.esporta(esportaDa, esportaA, formato)
    const url = URL.createObjectURL(res.data)
    const a = document.createElement('a')
    a.href = url; a.download = `cab_elenco_${esportaDa}_${esportaA}.${formato}`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-semibold text-slate-900">Archivio Etichette CAB</h1>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div className="relative w-80">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input className="input pl-9" placeholder="Cerca per codice..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        {isAdmin && (
          <div className="flex items-end gap-2 ml-auto">
            <div><label className="label text-[10px]">Da</label><input type="date" className="input" value={esportaDa} onChange={e => setEsportaDa(e.target.value)} /></div>
            <div><label className="label text-[10px]">A</label><input type="date" className="input" value={esportaA} onChange={e => setEsportaA(e.target.value)} /></div>
            <button className="btn-secondary text-xs flex items-center gap-1" onClick={() => esportaElenco('csv')}><Download size={13} /> CSV</button>
            <button className="btn-secondary text-xs flex items-center gap-1" onClick={() => esportaElenco('xlsx')}><Download size={13} /> XLSX</button>
          </div>
        )}
      </div>

      <div className="card overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 text-xs text-slate-500 uppercase">
              <th className="text-left px-3 py-2">Data</th>
              <th className="text-left px-3 py-2">Operazione</th>
              <th className="text-left px-3 py-2">Codice</th>
              <th className="text-left px-3 py-2">NMU</th>
              <th className="text-left px-3 py-2">Stato stampa</th>
              <th className="text-left px-3 py-2">Stato</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {isLoading && <tr><td colSpan={7} className="text-center py-8 text-slate-400">Caricamento...</td></tr>}
            {items.map((e: any) => (
              <tr key={e.id} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => setSelezionata(e)}>
                <td className="px-3 py-2">{new Date(e.creato_il).toLocaleDateString('it-IT')}</td>
                <td className="px-3 py-2">{e.operazione}</td>
                <td className="px-3 py-2 font-mono text-xs">{e.codice}</td>
                <td className="px-3 py-2">{e.nmu || '—'}</td>
                <td className="px-3 py-2"><span className="badge">{e.stato_stampa}</span></td>
                <td className="px-3 py-2">
                  <span className={`badge ${e.stato_record === 'ANNULLATA' ? 'bg-red-100 text-red-700' : ''}`}>{e.stato_record}</span>
                </td>
                <td className="px-3 py-2" />
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selezionata && (
        <div className="fixed inset-0 z-50 flex justify-end" style={{ background: 'rgba(0,0,0,0.3)' }} onClick={() => setSelezionata(null)}>
          <div className="bg-white w-[420px] h-full p-5 overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold">Dettaglio etichetta</h2>
              <button onClick={() => setSelezionata(null)}><X size={18} /></button>
            </div>
            <div className="space-y-2 text-sm">
              <p className="font-mono bg-slate-50 p-2 rounded">{selezionata.codice}</p>
              <p><b>Operazione:</b> {selezionata.operazione}</p>
              <p><b>NMU:</b> {selezionata.nmu || '—'} · <b>AFOR:</b> {selezionata.afor || '—'}</p>
              <p><b>Riferimento lavoro:</b> {selezionata.riferimento_lavoro || '—'}</p>
              <p><b>Stato stampa:</b> {selezionata.stato_stampa}</p>
              <p><b>Stato record:</b> {selezionata.stato_record}</p>
              {selezionata.annullata_motivo && <p><b>Motivo annullamento:</b> {selezionata.annullata_motivo}</p>}
              <p><b>Conservare fino al:</b> {selezionata.conservare_fino}</p>
            </div>

            <div className="flex flex-wrap gap-2 mt-4">
              <button className="btn-secondary text-xs flex items-center gap-1" onClick={() => scarica('zpl', selezionata.id, selezionata.codice)}><Download size={13} /> ZPL</button>
              <button className="btn-secondary text-xs flex items-center gap-1" onClick={() => scarica('svg', selezionata.id, selezionata.codice)}><Download size={13} /> SVG</button>
              <button className="btn-secondary text-xs flex items-center gap-1" onClick={() => scarica('pdf', selezionata.id, selezionata.codice)}><Download size={13} /> PDF</button>
              {selezionata.stato_record === 'ATTIVA' && (
                <>
                  <button className="btn-secondary text-xs flex items-center gap-1" onClick={() => setMotivoModal('ristampa')}><RotateCcw size={13} /> Ristampa</button>
                  {isAdmin && <button className="btn-secondary text-xs flex items-center gap-1 text-red-600" onClick={() => setMotivoModal('annulla')}><Ban size={13} /> Annulla</button>}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {motivoModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.45)' }}>
          <div className="bg-white rounded-xl p-5 w-96 space-y-3">
            <h3 className="font-semibold">{motivoModal === 'annulla' ? 'Annulla etichetta' : 'Ristampa etichetta'}</h3>
            <textarea className="input" rows={3} placeholder="Motivo (obbligatorio)" value={motivo} onChange={e => setMotivo(e.target.value)} />
            <div className="flex justify-end gap-2">
              <button className="btn-secondary" onClick={() => { setMotivoModal(null); setMotivo('') }}>Annulla</button>
              <button className="btn-primary" disabled={!motivo.trim() || annullaMut.isPending || ristampaMut.isPending}
                onClick={() => motivoModal === 'annulla' ? annullaMut.mutate() : ristampaMut.mutate()}>
                Conferma
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
